// UI-17 (Block I): the Team calendar on the v2 kit (design/business/mockups/team-calendar.html).
//
// What it shows (decision 33): the caller's own diary (their tasks, and their Google, subscribed
// and Outlook calendars) as "you", and from GET /api/calendar/team what the route allows of
// everyone else: approved leave as dates only, meetings with a team member invited, and tasks
// assigned to a team member. Until this block the page never called that route: it drew the
// caller's own tasks and filed them under whichever colleagues were on assignedTeam.
//
// Week, Month and Agenda tabs; member filter chips; event chips coloured by type; tap an empty
// slot to add. Phone (addendum 8): Agenda by default, Week is one day with arrows, Month shows
// dots and lists the tapped day. Event drag is UI-17b.
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '../api/client';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import useGoogleCalendarEvents from '../hooks/useGoogleCalendarEvents';
import useICalEvents from '../hooks/useICalEventsNew';
import { useOutlookEvents } from '../hooks/useOutlookEvents';
import {
  PageLayout, PageHeader, Button, TabGroup, TabPanel, Chip, Avatar, StatusPill, Banner, Skeleton,
  SidePanel, Sheet, ConfirmDialog, StatTile, useBreakpoint, v, HeaderButton,
} from '../ui';
import { currentBreakpoint } from '../ui/breakpoints';
import { formatTaskClock } from '../utils/taskTime';   // fix(task-dates): the shared formatter
import {
  localDay, dayToDate, addDays, startOfWeek, visibleRange, daysBetween, buildEvents, isOnDay, overlaps,
  slotOf, EVENT_TYPES, SLOTS,
} from './teamCalendar/calendarModel';
import './TeamCalendar.css';

const MEMBER_COLOURS = ['pastel-periwinkle', 'pastel-mint', 'pastel-blue', 'pastel-yellow', 'pastel-orange', 'pastel-lilac', 'pastel-green'];
const YOU_COLOUR = 'pastel-coral';
const PRIORITIES = ['low', 'medium', 'high'];

const fmtDay = (d, opts) => d.toLocaleDateString('en-GB', opts);
const shortDay = (d) => fmtDay(d, { day: 'numeric', month: 'short' });
const listJoin = (items) => (items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`);
const capitalise = (s) => s.charAt(0).toUpperCase() + s.slice(1);

const TeamCalendar = ({
  userSchedule = [],
  onEditTask,
  onDeleteTask,
  onAddTask,
}) => {
  const { currentUser } = useCurrentUser();
  const isMobile = useBreakpoint() === 'phone';

  // Phone opens on Agenda (addendum 8); read once, so turning the phone does not change the tab.
  const [view, setView] = useState(() => (currentBreakpoint() === 'phone' ? 'agenda' : 'week'));
  const [cursor, setCursor] = useState(() => new Date());
  const [hidden, setHidden] = useState(() => new Set());
  const [selectedDay, setSelectedDay] = useState(() => localDay(new Date()));
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [taskDeleteConfirm, setTaskDeleteConfirm] = useState(null);

  // ── A FAILED CALENDAR FETCH WAS INDISTINGUISHABLE FROM AN EMPTY WEEK ──────
  //
  // Carla, 2026-10-04: "NEITHER viewport distinguishes a failed fetch from an empty one.
  // That screen was not in my sitting at all, which means it was never checked by anyone."
  //
  // All three hooks have ALWAYS returned an `error` — the screen simply destructured only
  // `events`, with `= []` defaults that turn every failure into a clear diary. A team
  // calendar that reads as empty when it could not be loaded is the reassurance shape on the
  // one screen where being wrong means missing an appointment.
  const { events: googleEvents = [], error: googleError } = useGoogleCalendarEvents();

  // Get iCal events from user profile
  let userProfile = {};
  try { userProfile = JSON.parse(localStorage.getItem("userProfile") || "{}"); } catch { /* use empty default */ }
  // The ACCOUNT first — see App.js. A feed subscribed on another device used to be
  // invisible here, with nothing to say so.
  const icalUrl = currentUser?.preferences?.icalUrl
    || (userProfile.integrations?.ical?.connected ? userProfile.integrations.ical.url : null);
  const { events: icalEvents = [], error: icalError } = useICalEvents(icalUrl);

  // Get Outlook events
  const { events: outlookEvents = [], error: outlookError } = useOutlookEvents();

  // ── The team's diary: GET /api/calendar/team for the days on screen ──────
  // The fourth source that can fail, so it is in the banner with the other three.
  const range = useMemo(() => visibleRange(view, cursor), [view, cursor]);
  const fromDay = localDay(range.from);
  const toDay = localDay(range.to);
  const [team, setTeam] = useState({ events: [], members: [], leave: [] });
  const [teamError, setTeamError] = useState(null);
  const [teamLoading, setTeamLoading] = useState(true);
  const [teamAttempt, setTeamAttempt] = useState(0);

  useEffect(() => {
    let live = true;
    setTeamLoading(true);
    // Days, not instants: the route compares them with stored wall-clock times.
    api(`/api/calendar/team?start=${fromDay}&end=${toDay}`)
      .then((res) => res.json())
      .then((body) => {
        if (!live) return;
        setTeam({
          events: Array.isArray(body?.events) ? body.events : [],
          members: Array.isArray(body?.members) ? body.members : [],
          leave: Array.isArray(body?.leave) ? body.leave : [],
        });
        setTeamError(null);
      })
      .catch((err) => { if (live) setTeamError(err || new Error('Failed')); })
      .finally(() => { if (live) setTeamLoading(false); });
    return () => { live = false; };
  }, [fromDay, toDay, teamAttempt]);

  // Named individually so the banner can say WHICH feed failed. "A calendar could not be
  // loaded" sends her looking at all of them; naming it is the difference between a message
  // and an actionable one.
  const calendarErrors = [
    teamError ? 'the team calendar' : null,
    googleError ? 'Google Calendar' : null,
    icalError ? 'your subscribed calendar' : null,
    outlookError ? 'Outlook' : null,
  ].filter(Boolean);

  // ── People: you, then everyone the route returns (it excludes the caller) ──────
  const me = useMemo(() => ({ id: currentUser?.id ?? null, name: currentUser?.name || 'You' }), [currentUser?.id, currentUser?.name]);
  const people = useMemo(() => [
    { id: me.id, name: me.name, short: currentUser?.name ? `${me.name.split(' ')[0]} (you)` : 'You', color: YOU_COLOUR, you: true },
    ...team.members.map((m, i) => ({ id: m.id, name: m.name || m.email || 'Team member', short: (m.name || m.email || 'Team member').split(' ')[0], color: MEMBER_COLOURS[i % MEMBER_COLOURS.length] })),
  ], [me, team.members, currentUser?.name]);
  const personOf = useCallback((id) => people.find((p) => p.id === id) || null, [people]);

  const everyEvent = useMemo(() => buildEvents({
    me, members: team.members, tasks: userSchedule, google: googleEvents, ical: icalEvents, outlook: outlookEvents, team,
  }), [me, team, userSchedule, googleEvents, icalEvents, outlookEvents]);

  // What is on screen: the visible days, without the people switched off.
  const allEvents = useMemo(
    () => everyEvent.filter((e) => overlaps(e, fromDay, toDay) && !hidden.has(e.ownerId)),
    [everyEvent, fromDay, toDay, hidden],
  );
  const eventsOn = (day) => allEvents.filter((e) => isOnDay(e, day));

  // ── Navigation ─────────────────────────────────────────────────────────────
  const phoneDay = isMobile && view === 'week';           // Week on a phone is one day
  const step = (dir) => {
    setCursor((c) => {
      if (view === 'month') return new Date(c.getFullYear(), c.getMonth() + dir, 1);
      return addDays(c, dir * (phoneDay ? 1 : view === 'agenda' ? 14 : 7));
    });
  };
  const goToday = () => { const t = new Date(); setCursor(t); setSelectedDay(localDay(t)); };
  const todayDay = localDay(new Date());

  const rangeTitle = view === 'month'
    ? fmtDay(cursor, { month: 'long', year: 'numeric' })
    : phoneDay
      ? fmtDay(cursor, { weekday: 'long', day: 'numeric', month: 'long' })
      : `${shortDay(range.from)} to ${shortDay(range.to)} ${range.to.getFullYear()}`;

  const addOn = (date) => { if (onAddTask) onAddTask(date); };
  const openEvent = (event) => setSelectedEvent(event);

  // ── Header subline: live counts for the current week ──────────────────────
  const weekFrom = localDay(startOfWeek(new Date()));
  const weekTo = localDay(addDays(startOfWeek(new Date()), 6));
  // Inclusive of the whole of Sunday: the old count compared instants with Sunday at midnight,
  // so anything later on Sunday was not "this week".
  const thisWeek = everyEvent.filter((e) => overlaps(e, weekFrom, weekTo) && !hidden.has(e.ownerId));
  const thisWeekCount = thisWeek.filter((e) => e.kind !== 'away').length;
  const awayIds = [...new Set(thisWeek.filter((e) => e.kind === 'away').map((e) => e.ownerId || e.id))];
  const subline = [
    `${thisWeekCount} ${thisWeekCount === 1 ? 'event' : 'events'} this week`,
    awayIds.length ? `${awayIds.length} ${awayIds.length === 1 ? 'person' : 'people'} away` : 'everyone in',
    onAddTask ? (isMobile ? 'tap an empty slot to add' : 'click an empty slot to add') : null,
  ].filter(Boolean).join(' · ');

  const awayWhen = (personId) => {
    const days = thisWeek.filter((e) => e.kind === 'away' && e.ownerId === personId)
      .flatMap((e) => daysBetween(dayToDate(e.startDay < weekFrom ? weekFrom : e.startDay), dayToDate(e.endDay > weekTo ? weekTo : e.endDay)))
      .sort((a, b) => a - b);
    if (!days.length) return null;
    const a = fmtDay(days[0], { weekday: 'short' });
    const b = fmtDay(days[days.length - 1], { weekday: 'short' });
    return a === b ? a : `${a} to ${b}`;
  };

  const togglePerson = (id) => setHidden((h) => { const n = new Set(h); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  // ── Pieces ───────────────────────────────────────────────────────────────────
  const whoOf = (event) => {
    const p = personOf(event.ownerId);
    if (p) return p;
    return { id: null, name: event.kind === 'away' ? 'A former team member' : 'Someone', short: 'Someone', color: 'pastel-neutral' };
  };
  const timeOf = (event) => (event.allDay ? 'All day' : formatTaskClock(event.start));

  const EventChip = ({ event, compact = false }) => {
    const who = whoOf(event);
    const type = EVENT_TYPES[event.kind] || EVENT_TYPES.task;
    return (
      <button type="button" className={`tcal-chip${compact ? ' tcal-chip--compact' : ''}`} style={{ '--tcal-type': v(type.color) }}
        onClick={(e) => { e.stopPropagation(); openEvent(event); }}
        aria-label={`${event.title}, ${timeOf(event)}, ${who.you ? 'you' : who.name}, ${type.label}`}>
        {!compact && <Avatar name={who.name} size={24} color={who.color} title={who.you ? 'You' : who.name} />}
        <span className="tcal-chip__text">
          <span className="tcal-chip__title">{event.title}</span>
          {!compact && <span className="tcal-chip__time">{timeOf(event)}</span>}
        </span>
      </button>
    );
  };

  const AddSlot = ({ date, label }) => (onAddTask ? (
    <button type="button" className="tcal-add" onClick={() => addOn(date)} aria-label={`Add on ${fmtDay(date, { weekday: 'long', day: 'numeric', month: 'long' })}${label ? `, ${label.toLowerCase()}` : ''}`}>
      <span aria-hidden="true">+</span>
    </button>
  ) : null);

  // Desktop and tablet week: days across, parts of the day down. Scrolls sideways inside its own
  // box on a tablet (addendum 5: the team calendar week grid may), never the page.
  const WeekGrid = () => {
    const days = daysBetween(range.from, range.to);
    return (
      <div className="tcal-scroll">
        <div className="tcal-week" role="grid" aria-label={`Week of ${shortDay(range.from)}`}>
          <div className="tcal-week__row tcal-week__head" role="row">
            <span className="tcal-week__corner" role="columnheader" aria-label="Part of day" />
            {days.map((d) => {
              const day = localDay(d);
              const n = eventsOn(day).length;
              return (
                <div key={day} role="columnheader" className={`tcal-week__day${day === todayDay ? ' is-today' : ''}`}>
                  <span className="tcal-week__dow">{fmtDay(d, { weekday: 'short' })}</span>
                  <span className="tcal-week__num">{d.getDate()}</span>
                  <span className="tcal-week__count">{n ? `${n} ${n === 1 ? 'item' : 'items'}` : 'Free'}</span>
                </div>
              );
            })}
          </div>
          {SLOTS.map((slot) => (
            <div key={slot.key} className="tcal-week__row" role="row">
              <div className="tcal-week__slot" role="rowheader">
                <span className="tcal-week__slot-label">{slot.label}</span>
                <span className="tcal-week__slot-hours">{slot.hours}</span>
              </div>
              {days.map((d) => {
                const day = localDay(d);
                const inSlot = eventsOn(day).filter((e) => slotOf(e) === slot.key);
                return (
                  <div key={day} role="gridcell" className={`tcal-week__cell${day === todayDay ? ' is-today' : ''}`}>
                    {inSlot.map((e) => <EventChip key={e.id} event={e} />)}
                    <AddSlot date={d} label={slot.label} />
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // A list of one day's events, by part of the day (phone week, and the month's tapped day).
  const DayList = ({ date, showSlots }) => {
    const day = localDay(date);
    const list = eventsOn(day);
    if (!showSlots) {
      return list.length ? (
        <ul className="tcal-daylist">
          {list.map((e) => <li key={e.id}><AgendaRow event={e} /></li>)}
        </ul>
      ) : null;
    }
    return (
      <div className="tcal-dayslots">
        {SLOTS.map((slot) => {
          const inSlot = list.filter((e) => slotOf(e) === slot.key);
          return (
            <section key={slot.key} className="tcal-dayslot" aria-label={slot.label}>
              <h3 className="tcal-dayslot__label">{slot.label}</h3>
              <div className="tcal-dayslot__body">
                {inSlot.map((e) => <EventChip key={e.id} event={e} />)}
                <AddSlot date={date} label={slot.label} />
              </div>
            </section>
          );
        })}
      </div>
    );
  };

  // Phone week: one day, with arrows (the arrows are the range navigation above).
  const PhoneDay = () => {
    const day = localDay(cursor);
    const isToday = day === todayDay;
    const none = eventsOn(day).length === 0;
    return (
      <div className="tcal-phoneday">
        {none && calendarErrors.length === 0 && !teamLoading && (
          <p className="tcal-phoneday__empty" data-testid="team-calendar-day-empty">
            {isToday ? 'No events today' : 'Nothing on this day'}
          </p>
        )}
        <DayList date={cursor} showSlots />
      </div>
    );
  };

  const MonthGrid = () => {
    const days = daysBetween(range.from, range.to);
    const weeks = [];
    for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
    const dow = days.slice(0, 7).map((d) => fmtDay(d, { weekday: 'short' }));
    const selected = dayToDate(selectedDay);
    const selectedInView = selectedDay >= fromDay && selectedDay <= toDay;
    return (
      <>
        <div className={`tcal-month${isMobile ? ' tcal-month--dots' : ''}`}>
          <div className="tcal-month__dow" aria-hidden="true">{dow.map((n) => <span key={n}>{n}</span>)}</div>
          {weeks.map((w) => (
            <div key={localDay(w[0])} className="tcal-month__week">
              {w.map((d) => {
                const day = localDay(d);
                const list = eventsOn(day);
                const inMonth = d.getMonth() === cursor.getMonth();
                const cls = ['tcal-month__cell', !inMonth && 'is-out', day === todayDay && 'is-today', day === selectedDay && 'is-selected'].filter(Boolean).join(' ');
                const label = `${fmtDay(d, { weekday: 'long', day: 'numeric', month: 'long' })}, ${list.length ? `${list.length} ${list.length === 1 ? 'item' : 'items'}` : 'nothing booked'}`;
                if (isMobile) {
                  return (
                    <button key={day} type="button" className={cls} onClick={() => setSelectedDay(day)} aria-label={label} aria-pressed={day === selectedDay}>
                      <span className="tcal-month__num">{d.getDate()}</span>
                      <span className="tcal-month__dots" aria-hidden="true">
                        {list.slice(0, 3).map((e) => <span key={e.id} className="tcal-dot" style={{ '--tcal-type': v((EVENT_TYPES[e.kind] || EVENT_TYPES.task).color) }} />)}
                      </span>
                    </button>
                  );
                }
                return (
                  <div key={day} className={cls}>
                    <button type="button" className="tcal-month__num tcal-month__daybtn" onClick={() => setSelectedDay(day)} aria-label={label} aria-pressed={day === selectedDay}>
                      {d.getDate()}
                    </button>
                    {list.slice(0, 3).map((e) => <EventChip key={e.id} event={e} compact />)}
                    {list.length > 3 && (
                      <button type="button" className="tcal-month__more" onClick={() => setSelectedDay(day)}>+{list.length - 3} more</button>
                    )}
                    <AddSlot date={d} />
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        {selected && selectedInView && (
          <section className="tcal-picked" aria-label={`Events on ${fmtDay(selected, { weekday: 'long', day: 'numeric', month: 'long' })}`}>
            <div className="tcal-picked__head">
              <h3 className="tcal-picked__title">{fmtDay(selected, { weekday: 'long', day: 'numeric', month: 'long' })}</h3>
              {onAddTask && <Button variant="tertiary" size="sm" onClick={() => addOn(selected)}>+ Add on this day</Button>}
            </div>
            {eventsOn(selectedDay).length === 0 && calendarErrors.length === 0 && !teamLoading
              ? <p className="tcal-muted">Nothing booked on this day.</p>
              : <DayList date={selected} />}
          </section>
        )}
      </>
    );
  };

  const AgendaRow = ({ event }) => {
    const who = whoOf(event);
    const type = EVENT_TYPES[event.kind] || EVENT_TYPES.task;
    return (
      <button type="button" className="tcal-agenda__row" style={{ '--tcal-type': v(type.color) }} onClick={() => openEvent(event)}>
        <span className="tcal-agenda__time">{timeOf(event)}</span>
        <span className="tcal-agenda__bar" aria-hidden="true" />
        <span className="tcal-agenda__what">
          <span className="tcal-agenda__title">{event.title}</span>
          <span className="tcal-agenda__type">{type.label}</span>
        </span>
        <span className="tcal-agenda__who">
          <Avatar name={who.name} size={24} color={who.color} title={who.you ? 'You' : who.name} />
          <span className="tcal-agenda__name">{who.you ? 'You' : who.short}</span>
        </span>
      </button>
    );
  };

  const Agenda = () => {
    const days = daysBetween(range.from, range.to).filter((d) => eventsOn(localDay(d)).length > 0);
    return (
      <div className="tcal-agenda">
        {days.map((d) => {
          const day = localDay(d);
          return (
            <section key={day} className="tcal-agenda__day" aria-label={fmtDay(d, { weekday: 'long', day: 'numeric', month: 'long' })}>
              <div className="tcal-agenda__head">
                <span className="tcal-agenda__dow">{fmtDay(d, { weekday: 'short' })}</span>
                <span className="tcal-agenda__date">{shortDay(d)}</span>
                {day === todayDay && <StatusPill tone="success">Today</StatusPill>}
                {onAddTask && <Button variant="tertiary" size="sm" className="tcal-agenda__add" onClick={() => addOn(d)} aria-label={`Add on ${shortDay(d)}`}>+</Button>}
              </div>
              <ul className="tcal-daylist">
                {eventsOn(day).map((e) => <li key={e.id}><AgendaRow event={e} /></li>)}
              </ul>
            </section>
          );
        })}
        {isMobile && days.length === 0 && calendarErrors.length === 0 && !teamLoading && (
          <p className="tcal-muted tcal-agenda__empty">Nothing booked in these two weeks for the people shown.</p>
        )}
      </div>
    );
  };

  // ── The detail: a side panel on desktop and tablet, a sheet on a phone ────
  const Detail = () => {
    const e = selectedEvent;
    if (!e) return null;
    const who = whoOf(e);
    const type = EVENT_TYPES[e.kind] || EVENT_TYPES.task;
    const close = () => setSelectedEvent(null);
    const when = e.allDay
      ? (e.startDay === e.endDay
        ? `${fmtDay(dayToDate(e.startDay), { weekday: 'long', day: 'numeric', month: 'long' })}, all day`
        : `${fmtDay(dayToDate(e.startDay), { weekday: 'short', day: 'numeric', month: 'short' })} to ${fmtDay(dayToDate(e.endDay), { weekday: 'short', day: 'numeric', month: 'short' })}`)
      : `${fmtDay(e.start, { weekday: 'long', day: 'numeric', month: 'long' })}, ${formatTaskClock(e.start)}${e.end && e.end.getTime() !== e.start.getTime() ? ` to ${formatTaskClock(e.end)}` : ''}`;
    const priority = String(e.priority || '').toLowerCase();
    // Edit and Delete only on the caller's own Aidey tasks: a colleague's item is theirs to change.
    const canChange = e.mine && !!e.originalTask;
    const actions = canChange ? (
      <>
        {onDeleteTask && <Button variant="destructive" onClick={() => setTaskDeleteConfirm(e.originalTask)}>Delete</Button>}
        {onEditTask && <Button variant="secondary" onClick={() => { onEditTask(e.originalTask); close(); }}>Edit</Button>}
      </>
    ) : null;
    const body = (
      <div className="tcal-detail">
        {isMobile && <p className="tcal-detail__type">{type.label}</p>}
        <h3 className="tcal-detail__title">{e.title}</h3>
        <dl className="tcal-detail__facts">
          <dt>When</dt><dd>{when}</dd>
          <dt>Who</dt>
          <dd className="tcal-detail__who"><Avatar name={who.name} size={24} color={who.color} />{who.you ? `${who.name} (you)` : who.name}</dd>
          <dt>From</dt><dd>{e.source}</dd>
          {PRIORITIES.includes(priority) && (<><dt>Priority</dt><dd><StatusPill priority={priority} /></dd></>)}
          {e.location && (<><dt>Where</dt><dd>{e.location}</dd></>)}
        </dl>
        {e.details && <p className="tcal-detail__notes">{e.details}</p>}
        {!e.mine && e.kind !== 'away' && <p className="tcal-muted">On the team calendar because a team member is assigned or invited.</p>}
        {e.kind === 'away' && <p className="tcal-muted">Approved leave. The team calendar shows the dates only.</p>}
      </div>
    );
    return isMobile
      ? <Sheet open onClose={close} title={e.title} footer={actions} color={type.color}>{body}</Sheet>
      : <SidePanel open onClose={close} title={type.label} color={type.color} actions={actions}>{body}</SidePanel>;
  };

  const tabs = [
    { id: 'week', label: 'Week', color: 'pastel-blue' },
    { id: 'month', label: 'Month', color: 'pastel-yellow' },
    { id: 'agenda', label: 'Agenda', color: 'pastel-mint' },
  ];
  const rangeName = view === 'month' ? 'month' : view === 'agenda' ? 'fortnight' : 'week';
  const visiblePeople = people.filter((p) => !hidden.has(p.id)).length;

  return (
    <PageLayout data-page="team-calendar">
      <PageHeader
        eyebrow={{ label: 'Team', color: 'group-team' }}
        title="Team calendar"
        subline={subline}
        primary={onAddTask ? <HeaderButton primary onClick={() => onAddTask()}>+ Add event</HeaderButton> : null}
      />

      {/* ABOVE EVERY VIEW AND EVERY VIEWPORT — the failure half. Named feeds, because
          "a calendar failed" sends her checking every one. */}
      {calendarErrors.length > 0 && (
        <div data-testid="team-calendar-load-failed">
          <Banner tone="problem" action={teamError ? <Button variant="secondary" size="sm" onClick={() => setTeamAttempt((n) => n + 1)}>Try again</Button> : null}>
            {`${capitalise(listJoin(calendarErrors))} could not be loaded, so this is not your whole diary.`}
            {teamError?.message ? ` The server said: ${teamError.message.replace(/\.$/, '')}.` : ''}
          </Banner>
        </div>
      )}

      <div className="tcal-toolbar">
        <TabGroup tabs={tabs} value={view} onChange={setView} label="Calendar view" idPrefix="tcal" />
        <div className="tcal-nav">
          <button type="button" className="tcal-nav__btn" onClick={() => step(-1)} aria-label={phoneDay ? 'Previous day' : view === 'month' ? 'Previous month' : 'Previous'}>
            <span aria-hidden="true">←</span>
          </button>
          <Button variant="tertiary" size="sm" onClick={goToday}>Today</Button>
          <button type="button" className="tcal-nav__btn" onClick={() => step(1)} aria-label={phoneDay ? 'Next day' : view === 'month' ? 'Next month' : 'Next'}>
            <span aria-hidden="true">→</span>
          </button>
          <h2 className="tcal-nav__title" aria-live="polite">{rangeTitle}</h2>
        </div>
      </div>

      <div className="tcal-people">
        <span className="tcal-people__label">Showing</span>
        <div className="tcal-people__chips" role="group" aria-label="Show or hide people">
          {people.map((p) => {
            const away = awayWhen(p.id);
            return (
              <Chip key={p.id || 'me'} selected={!hidden.has(p.id)} onClick={() => togglePerson(p.id)} className="tcal-person">
                <Avatar name={p.name} size={24} color={p.color} />
                <span>{p.short}</span>
                {away && <span className="tcal-person__away">Away {away}</span>}
              </Chip>
            );
          })}
        </div>
        {hidden.size > 0 && <Button variant="tertiary" size="sm" onClick={() => setHidden(new Set())}>Show everyone</Button>}
        <ul className="tcal-legend" aria-label="Event types">
          {Object.entries(EVENT_TYPES).map(([k, t]) => (
            <li key={k}><span className="tcal-dot" style={{ '--tcal-type': v(t.color) }} aria-hidden="true" />{t.label}</li>
          ))}
        </ul>
      </div>

      <TabPanel idPrefix="tcal" tabId={view}>
        {teamLoading && <div className="tcal-loading"><Skeleton lines={2} label="Loading the team calendar" /></div>}
        {/* THE EMPTY HALF. One empty state above whichever view renders, rather than one inside
            each: views each growing their own empty state is how one ends up without it. Never
            while the team's diary is still loading: "loaded correctly" would be a guess. */}
        {!teamLoading && (
          <>
            {allEvents.length === 0 && calendarErrors.length === 0 && !isMobile && (
              <div data-testid="team-calendar-empty" className="tcal-empty">
                <p className="tcal-empty__title">Nothing in the diary {view === 'month' ? 'this month' : view === 'agenda' ? 'in these two weeks' : 'this week'}.</p>
                <p className="tcal-empty__text">
                  {hidden.size > 0
                    ? 'Some people are hidden. Show everyone to see their diaries.'
                    : <>This {rangeName} loaded correctly, there is genuinely nothing in it.</>}
                </p>
              </div>
            )}
          </>
        )}
        {view === 'week' && (phoneDay ? <PhoneDay /> : <WeekGrid />)}
        {view === 'month' && <MonthGrid />}
        {view === 'agenda' && <Agenda />}
      </TabPanel>

      <div className="tcal-stats">
        <StatTile card color="pastel-blue" label={`On screen this ${rangeName}`} value={allEvents.length} />
        <StatTile card color="pastel-lilac" label="People shown" value={`${visiblePeople}/${people.length}`} />
        <StatTile card color="pastel-mint" label="This week" value={thisWeekCount} />
      </div>

      <Detail />
      <ConfirmDialog
        open={taskDeleteConfirm !== null}
        onClose={() => setTaskDeleteConfirm(null)}
        onConfirm={() => { onDeleteTask(taskDeleteConfirm.id); setSelectedEvent(null); }}
        itemType="task"
        itemName={taskDeleteConfirm?.title || ''}
        warningText="This will permanently delete this task."
      />
    </PageLayout>
  );
};

export default TeamCalendar;
